import { AxiosError } from 'axios';

/**
 * Every API failure, normalised to one shape.
 *
 * Components never touch `AxiosError`. Without this, each call site ends up
 * writing its own `err.response?.data?.message ?? err.message ?? 'Error'` chain,
 * and they all differ subtly — so the same backend failure renders three
 * different ways depending on which screen you are on.
 */
export interface ApiError {
  /** HTTP status, or 0 when the request never reached the server. */
  status: number;

  /** Message safe to show a user. */
  message: string;

  /**
   * Field-level validation failures from a 422, keyed by camelCase field path.
   * Fed straight into React Hook Form's `setError` so the message lands on the
   * input that produced it.
   */
  fieldErrors?: Record<string, string[]>;

  /** Correlation id from a 500, which the user can quote to support. */
  correlationId?: string;
}

/** The error shape the API returns: `{ message, errors? }`. */
interface ApiErrorBody {
  message?: string;
  errors?: Record<string, string[]>;
}

/**
 * Converts anything thrown by Axios into an {@link ApiError}.
 *
 * @param error - Whatever was caught.
 * @returns A normalised error.
 */
export function toApiError(error: unknown): ApiError {
  // Idempotent. The response interceptor already rejects with an ApiError, and
  // every call site then calls this again in its `onError`. Without this guard
  // the second pass falls through to the `unknown` branch and replaces the real
  // message with "Something went wrong." — so the backend's explanation, and
  // even the timeout wording, never reached the user.
  if (isApiError(error)) {
    return error;
  }

  if (error instanceof AxiosError) {
    // No response at all: DNS failure, connection refused, timeout, or the user
    // going offline mid-request. Distinguished from a server error because the
    // advice differs — retry versus report.
    if (!error.response) {
      const isTimeout = error.code === 'ECONNABORTED';

      return {
        status: 0,
        message: isTimeout
          ? 'The request timed out. Check your connection and try again.'
          : 'Could not reach the server. Check your connection and try again.',
      };
    }

    const { status, data } = error.response;
    const body = (data ?? {}) as ApiErrorBody;

    return {
      status,
      message: body.message ?? defaultMessageFor(status),
      fieldErrors: body.errors,
    };
  }

  if (error instanceof Error) {
    return { status: 0, message: error.message };
  }

  return { status: 0, message: 'Something went wrong.' };
}

/** True when a value has already been normalised by {@link toApiError}. */
function isApiError(value: unknown): value is ApiError {
  return (
    typeof value === 'object' &&
    value !== null &&
    !(value instanceof Error) &&
    typeof (value as ApiError).status === 'number' &&
    typeof (value as ApiError).message === 'string'
  );
}

/** Fallback wording when the server sent no message. */
function defaultMessageFor(status: number): string {
  switch (status) {
    case 400:
      return 'That request was not valid.';
    case 401:
      return 'Your session has expired. Please sign in again.';
    case 403:
      return 'You do not have permission to do that.';
    case 404:
      return 'That record was not found.';
    case 409:
      return 'That conflicts with an existing record.';
    case 422:
      return 'Please correct the highlighted fields.';
    case 429:
      return 'Too many requests. Please wait a moment and try again.';
    default:
      return status >= 500
        ? 'Something went wrong on our side. Please try again.'
        : 'That request could not be completed.';
  }
}

/** True when the error carries per-field validation failures. */
export function hasFieldErrors(
  error: ApiError,
): error is ApiError & { fieldErrors: Record<string, string[]> } {
  return error.status === 422 && !!error.fieldErrors;
}
